import React, {
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';

import {
  AppIcon,
} from '@folio/stripes/core';
import stripesFinalForm from '@folio/stripes/final-form';
import {
  AccordionSet,
  AccordionStatus,
  Col,
  Pane,
  Paneset,
  Row,
  ExpandAllButton,
} from '@folio/stripes/components';

import {
  NavigateBackPaneFirstMenu,
  FormPaneFooter,
  StyledFormPane,
  RecordLinkageEdit,
} from '../../common';
import {
  APP_NAME,
  ICON_KEYS,
  FULL_PANE_WIDTH,
  JOB_TEMPLATE_FIELDS,
  RECORD_LINKAGE_PAGE_TYPE_JOB_TEMPLATE,
} from '../../../constants';
import {
  GeneralInformation,
  TaskTemplate,
} from './components';

const JobTemplateForm = ({
  form,
  pristine,
  submitting,
  handleSubmit,
  formTitle,
  metadata,
  onBack,
}) => {
  const {
    reset,
  } = form;
  const initialAccordionState = {
    general: true,
    metadata: false,
    taskTemplate: true,
  };

  const getFirstMenu = useCallback(() => (
    <NavigateBackPaneFirstMenu onClickHandler={onBack} />
  ), [onBack]);

  const getFooter = useCallback(() => (
    <FormPaneFooter
      form={form}
      pristine={pristine}
      submitting={submitting}
      handleSubmit={handleSubmit}
      onCancel={reset}
    />
  ), [pristine, submitting, reset, handleSubmit, form]);

  const getAppIcon = useCallback(() => (
    <AppIcon
      app={APP_NAME}
      iconKey={ICON_KEYS.APP}
    />
  ), []);

  return (
    <form>
      <Paneset isRoot>
        <Pane
          appIcon={getAppIcon()}
          defaultWidth={FULL_PANE_WIDTH}
          firstMenu={getFirstMenu()}
          footer={getFooter()}
          paneTitle={formTitle()}
          onClose={reset}
        >
          <StyledFormPane>
            <AccordionStatus>
              <Row end="xs">
                <Col xs>
                  <ExpandAllButton />
                </Col>
              </Row>
              <AccordionSet initialStatus={initialAccordionState}>
                <GeneralInformation metadata={metadata} />
                <RecordLinkageEdit pageType={RECORD_LINKAGE_PAGE_TYPE_JOB_TEMPLATE} />
                <TaskTemplate form={form} />
              </AccordionSet>
            </AccordionStatus>
          </StyledFormPane>
        </Pane>
      </Paneset>
    </form>
  );
};

JobTemplateForm.propTypes = {
  formTitle: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  form: PropTypes.shape({
    change: PropTypes.func.isRequired,
    mutators: PropTypes.shape({
      addAfterIndex: PropTypes.func.isRequired,
    }).isRequired,
    reset: PropTypes.func.isRequired,
  }),
  metadata: PropTypes.object
};

JobTemplateForm.defaultProps = {
  metadata: null
};

export default stripesFinalForm({
  initialValuesEqual: (a, b) => isEqual(a, b),
  subscription: { values: true },
  navigationCheck: true,
  mutators: {
    addAfterIndex: ([index, item], state, { changeValue }) => {
      changeValue(state, JOB_TEMPLATE_FIELDS.TASK_TEMPLATES, values => {
        const newTaskTemplates = [...values];

        newTaskTemplates.splice(index, 0, item);

        return newTaskTemplates;
      });
    },
    swapElementsByIndex: ([index1, index2], state, { changeValue }) => {
      changeValue(state, JOB_TEMPLATE_FIELDS.TASK_TEMPLATES, values => {
        const newTaskTemplates = [...values];

        [newTaskTemplates[index1], newTaskTemplates[index2]] = [newTaskTemplates[index2], newTaskTemplates[index1]];

        return newTaskTemplates;
      });
    },
  },
})(JobTemplateForm);
